import React from 'react';
import { Info, Users, MapPin, Target, MessageSquare } from 'lucide-react';

export default function ProjectContextStep({ formData, handleInputChange, errors }) {
  const familyStatusOptions = [
    'Célibataire',
    'Couple',
    'Famille avec enfants',
    'Retraité(e)',
    'Autre'
  ];

  const proximityOptions = [
    { value: 'same_building', label: 'Même immeuble' },
    { value: 'same_street', label: 'Même rue' },
    { value: 'same_city', label: 'Même ville' },
    { value: 'same_region', label: 'Même région' },
    { value: 'different_region', label: 'Région différente' }
  ];

  const investmentStrategyOptions = [
    'Location longue durée',
    'Revente rapide (< 5 ans)',
    'Transmission familiale',
    'Complément retraite',
    'Premier investissement locatif'
  ];

  const nearbyAttractionsOptions = [
    'École/Université prestigieuse',
    'Site touristique',
    'Lieu de pèlerinage',
    'Centre hospitalier',
    'Zone d\'activité économique',
    'Transport en commun',
    'Centre-ville',
    'Autre'
  ];

  return (
    <div className="space-y-8" data-testid="project-context-step">
      {/* Header explicatif */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
        <div className="flex items-start gap-3">
          <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-2">
              Pourquoi renseigner le contexte de votre projet ?
            </h3>
            <p className="text-blue-800 text-sm leading-relaxed">
              Ces informations <strong>augmentent significativement vos chances d'obtenir un financement</strong>. 
              Elles permettent au banquier de comprendre la cohérence et la pertinence de votre projet au-delà des seuls chiffres. 
              Un projet bien contextualisé inspire confiance et facilite la prise de décision.
            </p>
            <p className="text-blue-700 text-xs mt-2 italic">
              ✓ Toutes les questions sont facultatives • ✓ Vos réponses enrichiront automatiquement le dossier banquier
            </p>
          </div>
        </div>
      </div>

      {/* Section 1 : Contexte Personnel & Familial */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center gap-3 mb-6">
          <Users className="w-6 h-6 text-purple-600" />
          <h3 className="text-lg font-semibold text-slate-800">Contexte Personnel & Familial</h3>
        </div>

        {/* Situation familiale */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Quelle est votre situation familiale ? <span className="text-slate-400">(Facultatif)</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {familyStatusOptions.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => handleInputChange({ target: { name: 'contextFamilyStatus', value: status } })}
                data-testid={`context-family-status-${status.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                className={`p-3 rounded-lg border-2 text-sm font-medium transition ${
                  formData.contextFamilyStatus === status
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-slate-200 hover:border-purple-300 text-slate-600'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Projets familiaux */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Projets familiaux liés à ce bien <span className="text-slate-400">(Facultatif)</span>
          </label>
          <textarea
            name="contextFamilyProjects"
            value={formData.contextFamilyProjects || ''}
            onChange={handleInputChange}
            data-testid="context-family-projects-input"
            placeholder="Ex: Logement pour mes enfants durant leurs études, hébergement de proches, transmission future..."
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            rows="3"
          />
        </div>
      </div>

      {/* Section 2 : Proximité & Usage */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center gap-3 mb-6">
          <MapPin className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-semibold text-slate-800">Proximité & Usage Personnel</h3>
        </div>

        {/* Proximité géographique */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Êtes-vous proche du bien géographiquement ? <span className="text-slate-400">(Facultatif)</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {proximityOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleInputChange({ target: { name: 'contextProximity', value: option.value } })}
                data-testid={`context-proximity-${option.value}`}
                className={`p-3 rounded-lg border-2 text-sm font-medium transition text-left ${
                  formData.contextProximity === option.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 hover:border-blue-300 text-slate-600'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Usage personnel */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Envisagez-vous d'utiliser personnellement le bien ? <span className="text-slate-400">(Facultatif)</span>
          </label>
          <textarea
            name="contextPersonalUse"
            value={formData.contextPersonalUse || ''}
            onChange={handleInputChange}
            data-testid="context-personal-use-input"
            placeholder="Ex: Résidence secondaire pour vacances, week-ends en famille, bureaux temporaires..."
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="2"
          />
        </div>
      </div>

      {/* Section 3 : Environnement & Attractivité */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center gap-3 mb-6">
          <Target className="w-6 h-6 text-green-600" />
          <h3 className="text-lg font-semibold text-slate-800">Environnement & Attractivité</h3>
        </div>

        {/* Atouts du quartier */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Le bien est-il proche d'atouts spécifiques ? <span className="text-slate-400">(Facultatif)</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {nearbyAttractionsOptions.map((attraction) => {
              const selected = (formData.contextNearbyAttractions || []).includes(attraction);
              return (
                <button
                  key={attraction}
                  type="button"
                  onClick={() => {
                    const current = formData.contextNearbyAttractions || [];
                    const updated = selected
                      ? current.filter(a => a !== attraction)
                      : [...current, attraction];
                    handleInputChange({ target: { name: 'contextNearbyAttractions', value: updated } });
                  }}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition ${
                    selected
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-slate-200 hover:border-green-300 text-slate-600'
                  }`}
                >
                  {attraction}
                </button>
              );
            })}
          </div>
        </div>

        {/* Projets de développement */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Projets de développement local <span className="text-slate-400">(Facultatif)</span>
          </label>
          <textarea
            name="contextLocalDevelopment"
            value={formData.contextLocalDevelopment || ''}
            onChange={handleInputChange}
            data-testid="context-local-development-input"
            placeholder="Ex: Nouvelle ligne de métro, rénovation urbaine, zone commerciale en construction..."
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            rows="2"
          />
        </div>
      </div>

      {/* Section 4 : Stratégie d'Investissement */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center gap-3 mb-6">
          <Target className="w-6 h-6 text-orange-600" />
          <h3 className="text-lg font-semibold text-slate-800">Stratégie d'Investissement</h3>
        </div>

        {/* Stratégie long terme */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Quelle est votre stratégie à long terme ? <span className="text-slate-400">(Facultatif)</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {investmentStrategyOptions.map((strategy) => {
              const selected = (formData.contextInvestmentStrategy || []).includes(strategy);
              return (
                <button
                  key={strategy}
                  type="button"
                  onClick={() => {
                    const current = formData.contextInvestmentStrategy || [];
                    const updated = selected
                      ? current.filter(s => s !== strategy)
                      : [...current, strategy];
                    handleInputChange({ target: { name: 'contextInvestmentStrategy', value: updated } });
                  }}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition text-left ${
                    selected
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-slate-200 hover:border-orange-300 text-slate-600'
                  }`}
                >
                  {strategy}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 5 : Contexte Complet (Champ Libre) */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl shadow-md border-2 border-amber-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <MessageSquare className="w-6 h-6 text-amber-600" />
          <h3 className="text-lg font-semibold text-slate-800">Contexte Complet du Projet</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Décrivez librement tous les éléments qui rendent ce projet pertinent et cohérent. 
          Plus votre contexte est détaillé et authentique, plus il sera convaincant pour le banquier.
        </p>
        <textarea
          name="contextFreeText"
          value={formData.contextFreeText || ''}
          onChange={handleInputChange}
          data-testid="context-free-text-input"
          placeholder="Exemple : 'Ce projet s'inscrit dans une démarche de diversification patrimoniale. En tant que jeune couple avec deux enfants en bas âge, nous souhaitons constituer un patrimoine transmissible tout en bénéficiant de revenus complémentaires. Le bien est situé à proximité de notre résidence principale (même quartier), ce qui facilitera la gestion quotidienne. De plus, la proximité de l'école internationale et du futur tramway (livraison 2027) renforce l'attractivité locative à moyen terme...'"
          className="w-full px-4 py-3 border-2 border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
          rows="8"
        />
        <p className="text-xs text-amber-700 mt-3">
          💡 <strong>Conseil :</strong> Soyez authentique et précis. Les banquiers apprécient les projets bien réfléchis et documentés.
        </p>
      </div>
    </div>
  );
}
